import { useState, useEffect, useMemo } from 'react';
import {
  DEFAULT_LOCAL_SPEND_TOGGLE_YEAR,
  OPPS_DATA,
  SPENDS_AND_OPPORTUNITIES__MONTHLY_OVERTIME,
  SPENDS_AND_OPPORTUNITIES__MONTHLY_OVERTIME_LOCAL,
  SPENDS_AND_OPPORTUNITIES__WEEKLY_OVERTIME,
  SPENDS_AND_OPPORTUNITIES__WEEKLY_OVERTIME_LOCAL,
  INITIAL_PLAN_SPEND__OVERTIME_WEEKLY,
  INITIAL_PLAN_SPEND__OVERTIME_MONTHLY,
  INITIAL_PLAN_OPPORTUNITIES__OVERTIME_WEEKLY,
  INITIAL_PLAN_OPPORTUNITIES__OVERTIME_MONTHLY,
  WEEKLY,
  fiscalMonths,
} from '../constants';
import { getOTPayload, getSpendsAndOppsData } from '../service/SpendAndOppurtunitiesService';

export const useOverTimeDataHooks = ({
  selectedStore,
  currentYear,
  viewType,
  userStores,
  dataType,
  spendToggleState,
  channels = null,
  isInitialPlan = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const branchOptions = useMemo(
    () =>
      userStores
        .map((store) => ({
          label: store.branch_name,
          value: store.branch_number,
          ...store,
        }))
        .sort((a, b) => a.value - b.value),
    [userStores]
  );

  const compareWith = useMemo(() => {
    if (branchOptions.find((store) => store.value === selectedStore)?.label === 'GALE Markets') {
      return [
        { label: 'None', value: 'None' },
        { label: 'Store Region', value: 'Store Region' },
      ];
    }
    return [
      { label: 'None', value: 'None' },
      { label: 'GALE Markets', value: 'GALE Markets' },
      { label: 'Store Region', value: 'Store Region' },
    ];
  }, [branchOptions, selectedStore]);

  const [xAxisOOTData, setXAxisOOTData] = useState([]);

  const [currActualValues, setCurrActualValues] = useState([]);
  const [currPlannedValues, setCurrPlannedValues] = useState([]);

  const [currActualValuesWeekly, setCurrActualValuesWeekly] = useState([]);
  const [currPlannedValuesWeekly, setCurrPlannedValuesWeekly] = useState([]);

  const [overTimeSeries, setOverTimeSeries] = useState([]);
  const [overTimeLegend, setOverTimeLegend] = useState([
    `${currentYear} Planned - ${branchOptions?.find((store) => store?.value === selectedStore)?.label}`,
    `${currentYear} Actual - ${branchOptions?.find((store) => store?.value === selectedStore)?.label}`,
  ]);
  const [selectedYears, setSelectedYears] = useState([currentYear]);
  const [selectedComparisonStore, setSelectedComparisonStore] = useState('');
  const [displayWeekly, setDisplayWeekly] = useState(false);

  // Centralized helper to get store label - avoids repeated lookups
  const getStoreLabel = () => branchOptions?.find((store) => store?.value === selectedStore)?.label;

  // Helper function to update past years data
  /**
   * @param pastYears
   * @param actualKey
   * @param plannedKey
   * @param actualValue
   * @param plannedValue
   */
  function updatePastYears(pastYears, actualKey, plannedKey, actualValue, plannedValue) {
    if (pastYears[actualKey]?.length || pastYears[plannedKey]?.length) {
      pastYears[actualKey] = [...pastYears[actualKey], actualValue];
      pastYears[plannedKey] = [...pastYears[plannedKey], plannedValue];
    } else {
      pastYears[actualKey] = [actualValue];
      pastYears[plannedKey] = [plannedValue];
    }
  }

  // Unified helper to get data values based on type and data kind (opps or spends)
  /**
   * @param entry
   * @param type
   * @param displayWeekly
   * @param isOpps
   */
  function getDataValues(entry, type, displayWeekly, isOpps) {
    const avgField = isOpps ? 'opps_avg' : 'spend_avg';
    const actualField = isOpps ? 'actual_opps' : 'actual_spend';
    const planAvgField = isOpps ? 'opps_plan_avg' : 'spend_plan_avg';
    const planField = isOpps ? 'opps_plan' : 'spend_plan';

    const actual = type === WEEKLY ? entry[avgField] : entry[actualField];
    const planned = type === WEEKLY ? entry[planAvgField] : entry[planField];
    const actualWeekly = type === WEEKLY && !displayWeekly ? entry[actualField] : actual;
    const plannedWeekly = type === WEEKLY && !displayWeekly ? entry[planField] : planned;

    return { actual, planned, actualWeekly, plannedWeekly };
  }

  // Wrapper functions to maintain backward compatibility
  /**
   * @param entry
   * @param type
   * @param displayWeekly
   */
  function getOppsValues(entry, type, displayWeekly) {
    const { actual, planned, actualWeekly, plannedWeekly } = getDataValues(
      entry,
      type,
      displayWeekly,
      true
    );

    return {
      actual_opps: actual,
      planned_opps: planned,
      actual_opps_weekly: actualWeekly,
      planned_opps_weekly: plannedWeekly,
    };
  }

  /**
   * @param entry
   * @param type
   * @param displayWeekly
   */
  function getSpendsValues(entry, type, displayWeekly) {
    const { actual, planned, actualWeekly, plannedWeekly } = getDataValues(
      entry,
      type,
      displayWeekly,
      false
    );

    return {
      actual_spend: actual,
      planned_spend: planned,
      actual_spend_weekly: actualWeekly,
      planned_spend_weekly: plannedWeekly,
    };
  }

  // Unified helper function to process current year data (works for both opps and spends)
  /**
   * @param options
   */
  function processCurrentYearData(options) {
    const {
      entry,
      actualValue,
      plannedValue,
      actualWeeklyValue,
      plannedWeeklyValue,
      type,
      displayWeekly,
      hasRegionID,
      isOpps,
      arrays,
    } = options;

    const {
      actualValues,
      plannedValues,
      actualValesWeekly,
      plannedValuesWeekly,
      storeAggregatedGale,
      storeAggregatedRegion,
    } = arrays;

    actualValues.push(actualValue);
    if (type === WEEKLY) {
      actualValesWeekly.push(actualWeeklyValue);
    }
    plannedValues.push(plannedValue?.toFixed(2));

    // Handle weekly planned values based on data type
    const shouldPushPlannedWeekly = isOpps ? type === WEEKLY && !displayWeekly : type === WEEKLY;

    if (shouldPushPlannedWeekly) {
      plannedValuesWeekly.push(plannedWeeklyValue);
    }

    if (hasRegionID) {
      const galeAvgField = isOpps ? 'gale_avg_opps' : 'gale_avg_spend';
      const regionAvgField = isOpps ? 'region_avg_opps' : 'region_avg_spend';

      if (entry[galeAvgField]) {
        storeAggregatedGale.push(entry[galeAvgField]?.toFixed(2));
      }
      if (entry[regionAvgField]) {
        storeAggregatedRegion.push(entry[regionAvgField]?.toFixed(2));
      }
    }
  }

  // Wrapper for opps (maintains original interface)
  /**
   * @param options
   */
  function processCurrentYearOpps(options) {
    const { actual_opps, planned_opps, actual_opps_weekly, planned_opps_weekly, ...rest } = options;

    processCurrentYearData({
      ...rest,
      actualValue: actual_opps,
      plannedValue: planned_opps,
      actualWeeklyValue: actual_opps_weekly,
      plannedWeeklyValue: planned_opps_weekly,
      isOpps: true,
    });
  }

  // Wrapper for spends (maintains original interface)
  /**
   * @param options
   */
  function processCurrentYearSpends(options) {
    const { actual_spend, planned_spend, actual_spend_weekly, planned_spend_weekly, ...rest } =
      options;

    processCurrentYearData({
      ...rest,
      actualValue: actual_spend,
      plannedValue: planned_spend,
      actualWeeklyValue: actual_spend_weekly,
      plannedWeeklyValue: planned_spend_weekly,
      isOpps: false,
    });
  }

  // Helper function to process entry based on data type
  /**
   * @param options
   */
  function processEntry(options) {
    const {
      entry,
      isCurrentYear,
      type,
      dataType,
      displayWeekly,
      hasRegionID,
      actualYearKey,
      plannedYearKey,
      arrays,
      pastYears,
    } = options;

    if (dataType === OPPS_DATA) {
      const { actual_opps, planned_opps, actual_opps_weekly, planned_opps_weekly } = getOppsValues(
        entry,
        type,
        displayWeekly
      );

      if (isCurrentYear) {
        processCurrentYearOpps({
          entry,
          actual_opps,
          planned_opps,
          actual_opps_weekly,
          planned_opps_weekly,
          type,
          displayWeekly,
          hasRegionID,
          arrays,
        });
      } else {
        updatePastYears(
          pastYears,
          actualYearKey,
          plannedYearKey,
          actual_opps_weekly?.toFixed(2),
          planned_opps_weekly?.toFixed(2)
        );
      }
    } else {
      const { actual_spend, planned_spend, actual_spend_weekly, planned_spend_weekly } =
        getSpendsValues(entry, type, displayWeekly);

      if (isCurrentYear) {
        processCurrentYearSpends({
          entry,
          actual_spend,
          planned_spend,
          actual_spend_weekly,
          planned_spend_weekly,
          type,
          displayWeekly,
          hasRegionID,
          arrays,
        });
      } else {
        updatePastYears(
          pastYears,
          actualYearKey,
          plannedYearKey,
          actual_spend_weekly?.toFixed(2),
          planned_spend_weekly?.toFixed(2)
        );
      }
    }
  }

  /**
   * @param data
   * @param type
   */
  function transformOverTimeData(data, type) {
    let actualValues = [],
      plannedValues = [],
      actualValesWeekly = [],
      plannedValuesWeekly = [],
      storeAggregatedGale = [],
      storeAggregatedRegion = [],
      pastYears = {},
      hasRegionID = false;

    const filteredData = data.filter((eachData) => eachData.fiscal_month !== null);
    let sortArr = [];

    if (type === WEEKLY) {
      sortArr = filteredData.slice().sort((a, b) => a.fiscal_week - b.fiscal_week);
    } else {
      sortArr = filteredData.slice().sort((a, b) => a.fiscal_month - b.fiscal_month);
    }

    if (branchOptions.find((store) => store.value === selectedStore).region) {
      hasRegionID = true;
    }

    // Create arrays object to pass to processEntry
    const arrays = {
      actualValues,
      plannedValues,
      actualValesWeekly,
      plannedValuesWeekly,
      storeAggregatedGale,
      storeAggregatedRegion,
    };

    const storeLabel = getStoreLabel();

    sortArr.forEach((entry) => {
      const isCurrentYear = entry.fiscal_year === currentYear;

      // Create keys for past years data once to avoid duplication
      const actualYearKey = `${entry.fiscal_year} Actual - ${storeLabel}`;
      const plannedYearKey = `${entry.fiscal_year} Planned - ${storeLabel}`;

      processEntry({
        entry,
        isCurrentYear,
        type,
        dataType,
        displayWeekly,
        hasRegionID,
        actualYearKey,
        plannedYearKey,
        arrays,
        pastYears,
      });
    });

    // Set X-axis values
    const xAxisValues = [
      ...new Set(
        sortArr?.map((item) =>
          type === WEEKLY ? `W${item?.fiscal_week}` : fiscalMonths[item?.fiscal_month]
        )
      ),
    ];

    setCurrActualValues(actualValues);
    setCurrActualValuesWeekly(actualValesWeekly);
    setCurrPlannedValues(plannedValues);
    setCurrPlannedValuesWeekly(plannedValuesWeekly);
    setXAxisOOTData(xAxisValues);

    const legendValue = selectedYears.map(String);

    if (selectedYears.includes(currentYear)) {
      setOverTimeLegend([
        `${currentYear} Planned - ${storeLabel}`,
        `${currentYear} Actual - ${storeLabel}`,
        ...generateLegends(legendValue.filter((x) => !x.includes(currentYear))),
      ]);
    } else {
      setOverTimeLegend(generateLegends(legendValue));
    }

    // Create series data with a common style configuration
    const lineConfig = {
      type: 'line',
      lineStyle: { width: 2 },
      itemStyle: { borderWidth: 2, borderColor: '#a5b0af' },
    };

    // Build series array
    const seriesValueArr = [
      {
        name: `${currentYear} Actual - ${storeLabel}`,
        data: !displayWeekly && type === WEEKLY ? actualValesWeekly : actualValues,
        ...lineConfig,
      },
      {
        name: `${currentYear} Planned - ${storeLabel}`,
        data: !displayWeekly && type === WEEKLY ? plannedValuesWeekly : plannedValues,
        ...lineConfig,
      },
    ];

    // Add comparison data
    if (selectedComparisonStore === 'GALE Markets' && storeAggregatedGale?.length) {
      seriesValueArr.push({
        name: 'GALE Markets Average',
        data: storeAggregatedGale,
        ...lineConfig,
      });
    }

    if (selectedComparisonStore === 'Store Region' && storeAggregatedRegion?.length) {
      seriesValueArr.push({
        name: 'Regional Store Average',
        data: storeAggregatedRegion,
        ...lineConfig,
      });
    }

    // Add past years data
    if (Object.keys(pastYears)?.length) {
      Object.keys(pastYears).forEach((year) => {
        seriesValueArr.push({
          name: year,
          data: pastYears[year],
          ...lineConfig,
        });
      });
    }

    // Sort seriesValueArr by name in descending order with null/undefined check
    seriesValueArr?.sort((a, b) => {
      const nameA = a?.name || '';
      const nameB = b?.name || '';

      return nameB?.localeCompare(nameA);
    });

    setOverTimeSeries(seriesValueArr);
  }

  const generateLegends = (years) => {
    const storeLabel = getStoreLabel();

    return years.flatMap((year) => [
      `${year} Planned - ${storeLabel}`,
      `${year} Actual - ${storeLabel}`,
    ]);
  };

  const handleOOTStoreChange = (newSelectedStore) => {
    setSelectedComparisonStore(newSelectedStore);
  };
  const handleYearChange = (newSelectedYears) => {
    if (!newSelectedYears?.length) {
      setOverTimeSeries([]);
    }
    setSelectedYears(newSelectedYears);
  };

  const fetchAndTransformData = async () => {
    setIsLoading(true);
    setError(null);
    setCurrActualValuesWeekly(null);
    setCurrPlannedValuesWeekly(null);
    try {
      let monthlyKey, weeklyKey;

      if (isInitialPlan) {
        // Use Initial Plan component names
        if (dataType === OPPS_DATA) {
          monthlyKey = INITIAL_PLAN_OPPORTUNITIES__OVERTIME_MONTHLY;
          weeklyKey = INITIAL_PLAN_OPPORTUNITIES__OVERTIME_WEEKLY;
        } else {
          monthlyKey = INITIAL_PLAN_SPEND__OVERTIME_MONTHLY;
          weeklyKey = INITIAL_PLAN_SPEND__OVERTIME_WEEKLY;
        }
      } else {
        // Use regular Spend and Opportunities component names
        monthlyKey = !spendToggleState
          ? SPENDS_AND_OPPORTUNITIES__MONTHLY_OVERTIME
          : SPENDS_AND_OPPORTUNITIES__MONTHLY_OVERTIME_LOCAL;
        weeklyKey = !spendToggleState
          ? SPENDS_AND_OPPORTUNITIES__WEEKLY_OVERTIME
          : SPENDS_AND_OPPORTUNITIES__WEEKLY_OVERTIME_LOCAL;
      }

      const key = viewType === 'Monthly' ? monthlyKey : weeklyKey;
      const payload = getOTPayload(key, selectedStore, selectedYears, channels);
      const response = await getSpendsAndOppsData(payload, key, dataType);

      if (response.success) {
        transformOverTimeData(response.data[key].result, viewType);
      } else {
        throw new Error('Data fetch was successful but data structure was unexpected.');
      }
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedStore !== undefined && selectedYears.length > 0 && viewType !== undefined) {
        if (spendToggleState) {
          const filteredYears = selectedYears?.filter(
            (year) => year >= DEFAULT_LOCAL_SPEND_TOGGLE_YEAR
          );

          if (filteredYears?.length !== selectedYears?.length) {
            handleYearChange(filteredYears);
            return; // Exit early to avoid calling fetchAndTransformData with incorrect years
          }
        }
        fetchAndTransformData();
      }
    }, 1000); // 1000ms delay

    // Cleanup function to clear the timeout if the value changes before the delay is over
    return () => clearTimeout(timer);
  }, [
    selectedStore,
    viewType,
    selectedComparisonStore,
    selectedYears,
    spendToggleState,
    displayWeekly,
    channels,
    isInitialPlan,
  ]);

  return {
    selectedYears,
    isLoading,
    error,
    overTimeSeries,
    currActualValues,
    currPlannedValues,
    overTimeLegend,
    xAxisOOTData,
    handleYearChange,
    handleOOTStoreChange,
    selectedComparisonStore,
    compareWith,
    displayWeekly,
    setDisplayWeekly,
    currActualValuesWeekly,
    currPlannedValuesWeekly,
  };
};
